"use client";

import { useId, useState, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import {
  ArrowForwardIcon,
  ChevronRightIcon,
  ExpandIcon,
  LockIcon,
  NotificationIcon,
} from "@/components/appIcons";
import { FallbackImage } from "@/components/fallback-image";
import { FormatIcon } from "@/components/format-icon";
import { Link } from "@/components/link";
import { useDealOfferPriceAlert } from "@/components/marketplace/deal-offer-price-alert-context";
import { OfferPriceHistory } from "@/components/marketplace/deal-offer-price-history-modal";
import { DealOfferActionCta } from "@/components/marketplace/deal-offer-shared";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import {
  dealOfferShippingCountsTowardTotal,
  effectiveDealPrice,
  isInternationalDealOffer,
} from "@/components/marketplace/deal-offers-utils";
import { OfferRowHistorySignal } from "@/components/marketplace/offer-row-history-signal";
import { UsedOfferRow } from "@/components/marketplace/UsedOfferRow";
import { useOfferMediaPacking } from "@/components/marketplace/useOfferMediaPacking";
import { Price } from "@/components/Price";
import { Button } from "@/components/ui/button";
import { getCurrentBrowserRedirectPath, setRedirectAfterLogin } from "@/lib/redirect-after-login";
import { cn } from "@/lib/utils";
import { dealOfferShippingPriceForDisplay } from "@garimpa/lib/dealOfferShippingPriceForDisplay";

/** Phone: image beside two lines. From a 36rem container: chevron, image, listing, history, price, store. */
const OFFER_ROW_COLUMNS =
  "grid-cols-[3rem_minmax(0,1fr)_9rem] @xl:grid-cols-[1rem_3rem_minmax(0,1fr)_7rem_6.5rem_9.5rem]";
/** Wide-layout columns; the list header uses the same template. */
export const OFFER_ROW_WIDE_COLUMNS =
  "@4xl:grid-cols-[1rem_3.25rem_minmax(0,1fr)_8rem_8.5rem_11rem]";

export interface OfferRowProps {
  offer: DealOffer;
  currency: string;
  cheapest: boolean;
  /** Optional catalog packing shown instead of condition and origin. */
  listingDetails?: string | null;
  /** The release the list is shown for; its own "View edition" link is hidden. */
  contextReleaseId?: string;
  defaultOpen?: boolean;
  /** Release page only: links to sibling editions this offer also matched. */
  editionContext?: ReactNode;
  /** Admin-only tools (AdminOfferEmitButton), shown in the open panel. */
  adminAction?: ReactNode;
}

function offerCurrency(offer: DealOffer) {
  return offer.priceHistoryCurrency ?? offer.currentCurrency;
}

function shippingSupplement(offer: DealOffer) {
  if (!isInternationalDealOffer(offer)) return null;
  return dealOfferShippingPriceForDisplay({
    shippingPrice: offer.currentPriceShipping,
    shippingCountsTowardTotal: dealOfferShippingCountsTowardTotal(offer),
  });
}

function OfferRowImage({ offer, className }: { offer: DealOffer; className?: string }) {
  return (
    <span
      className={cn(
        "relative size-12 shrink-0 overflow-hidden rounded-md border border-border/60 bg-secondary/40 @4xl:size-[3.25rem]",
        className,
      )}
    >
      {offer.imageUrl ? (
        <FallbackImage src={offer.imageUrl} alt="" sizes="52px" objectFit="contain" />
      ) : (
        <span className="flex size-full items-center justify-center text-primary/70">
          <FormatIcon format={offer.formatCategory ?? "other"} size={22} />
        </span>
      )}
    </span>
  );
}

function OfferRowPrice({ offer, className }: { offer: DealOffer; className?: string }) {
  const t = useTranslations("shared.affiliates");
  const currency = offerCurrency(offer);
  const supplement = shippingSupplement(offer);
  return (
    <div className={cn("min-w-0 text-right", className)}>
      <Price
        amount={offer.currentPrice ?? effectiveDealPrice(offer)}
        currency={currency}
        fallback="—"
        className="text-lg font-black leading-tight tracking-tight"
      />
      {supplement && (
        <p className="text-xs text-muted-foreground">
          {t.rich("shippingSupplement", {
            price: () => <Price amount={supplement} currency={currency} plain />,
          })}
        </p>
      )}
    </div>
  );
}

function OfferRowMembersHistory() {
  const t = useTranslations("shared.affiliates");
  const rememberCurrentPage = () => {
    const redirectPath = getCurrentBrowserRedirectPath();
    if (redirectPath) setRedirectAfterLogin(redirectPath);
  };
  return (
    <div className="flex min-h-28 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border px-4 py-5 text-center">
      <LockIcon aria-hidden="true" className="text-lg text-muted-foreground" />
      <p className="text-sm font-semibold text-foreground">{t("offerHistoryMembersOnly")}</p>
      <Link
        to="/login"
        rel="nofollow"
        onClick={rememberCurrentPage}
        className="inline-flex min-h-11 items-center rounded-md bg-primary px-4 sm:min-h-10 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {t("offerHistoryMembersCta")}
      </Link>
    </div>
  );
}

function OfferRowPanelActions({
  adminAction,
  contextReleaseId,
  offer,
}: {
  adminAction?: ReactNode;
  contextReleaseId?: string;
  offer: DealOffer;
}) {
  const t = useTranslations("shared.affiliates");
  const onPriceAlert = useDealOfferPriceAlert();
  const currency = offerCurrency(offer);
  const shipping = shippingSupplement(offer);

  return (
    <div className="flex flex-col gap-3">
      {shipping && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t.rich("offerShippingBreakdown", {
            item: () => <Price amount={offer.currentPrice} currency={currency} plain />,
            shipping: () => <Price amount={shipping} currency={currency} plain />,
          })}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {onPriceAlert && (
          <Button
            variant="outline"
            className="min-h-11 font-semibold lg:min-h-9"
            onClick={onPriceAlert}
          >
            <NotificationIcon aria-hidden="true" />
            {t("priceAlertCta")}
          </Button>
        )}
        {offer.matchCatalogId && offer.matchCatalogId !== contextReleaseId && (
          <Link
            to="/release/$id"
            params={{ id: offer.matchCatalogId }}
            className="inline-flex min-h-11 items-center gap-1 px-1.5 text-[13px] font-semibold text-primary hover:underline lg:min-h-9"
          >
            {t("offerViewEdition")}
            <ArrowForwardIcon aria-hidden="true" className="text-sm" />
          </Link>
        )}
        {adminAction}
      </div>
    </div>
  );
}

/** Cheapest badge, edition packing and seller on one truncating line below the title. */
function OfferRowListingText({
  cheapest,
  offer,
  listingDetails,
}: {
  cheapest: boolean;
  offer: DealOffer;
  listingDetails?: string | null;
}) {
  const t = useTranslations("shared.affiliates");
  const offerDetails = useOfferMediaPacking(offer.releaseMediaFormatSummary, offer.formatCategory);
  const details = listingDetails === undefined ? offerDetails : listingDetails;
  const meta = [details, offer.sellerName].filter(Boolean).join(" · ");
  return (
    <span className="flex min-w-0 items-center gap-2">
      {cheapest && (
        <span className="shrink-0 rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
          {t("offerLowestPriceBadge")}
        </span>
      )}
      {meta && (
        <span className="min-w-0 truncate text-xs text-muted-foreground" title={meta}>
          {meta}
        </span>
      )}
    </span>
  );
}

/**
 * One store offer in a list: a disclosure button (listing), the offer against its own history,
 * the price, and the store CTA. Two lines beside the image on phones; one line from a 36rem
 * container. Each row owns its open state, so several rows can be open at once.
 */
export function OfferRow(props: OfferRowProps) {
  return props.offer.currentCondition === "used" ? (
    <UsedOfferRow {...props} />
  ) : (
    <NewOfferRow {...props} />
  );
}

function NewOfferRow({
  offer,
  currency,
  cheapest,
  contextReleaseId,
  defaultOpen = false,
  editionContext,
  adminAction,
  listingDetails,
}: OfferRowProps) {
  const t = useTranslations("shared.affiliates");
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();

  return (
    <li
      data-offer-id={offer.offerId}
      data-open={open ? "true" : "false"}
      className="border-b border-border/60 transition-colors hover:bg-secondary/40 last:border-b-0 data-[open=true]:bg-secondary/30"
    >
      <div
        className={cn(
          "relative grid items-center gap-x-3 gap-y-1.5 px-4 py-3 @xl:py-2.5 @4xl:gap-x-4 @4xl:pl-3 @4xl:pr-5",
          OFFER_ROW_COLUMNS,
          OFFER_ROW_WIDE_COLUMNS,
        )}
      >
        <ChevronRightIcon
          aria-hidden="true"
          className={cn(
            "hidden shrink-0 text-base text-muted-foreground transition-transform motion-reduce:transition-none @xl:col-start-1 @xl:row-start-1 @xl:block",
            open && "rotate-90 text-primary",
          )}
        />
        <OfferRowImage
          offer={offer}
          className="col-start-1 row-span-2 row-start-1 @xl:col-start-2 @xl:row-span-1"
        />
        <button
          type="button"
          aria-label={offer.title}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((current) => !current)}
          className="col-start-2 row-start-1 flex min-w-0 cursor-pointer flex-col gap-1 rounded-md text-left after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-primary @xl:col-start-3"
        >
          <span
            className="truncate text-sm font-bold leading-snug text-foreground"
            title={offer.title}
          >
            {offer.title}
          </span>
          <OfferRowListingText offer={offer} cheapest={cheapest} listingDetails={listingDetails} />
        </button>
        <OfferRowPrice offer={offer} className="col-start-3 row-start-1 @xl:col-start-5" />
        <div className="col-start-2 row-start-2 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 @xl:col-start-4 @xl:row-start-1">
          <OfferRowHistorySignal offer={offer} />
          <span
            aria-hidden="true"
            className="inline-flex items-center gap-0.5 text-xs text-muted-foreground @xl:hidden"
          >
            {t("offerHistoryAction")}
            <ExpandIcon className={cn("text-sm transition-transform", open && "rotate-180")} />
          </span>
        </div>
        <div className="pointer-events-none relative z-10 col-start-3 row-start-2 min-w-0 @xl:col-start-6 @xl:row-start-1">
          <DealOfferActionCta
            offer={offer}
            providerLabelOnly
            variant="compact"
            className="pointer-events-auto w-full"
          />
        </div>
        {editionContext && (
          <div className="pointer-events-none relative z-10 col-[2/-1] min-w-0 [&_a]:pointer-events-auto @xl:col-[3/-1]">
            {editionContext}
          </div>
        )}
      </div>
      {open && (
        <div
          id={panelId}
          className="grid gap-3 px-4 pb-4 @xl:pl-[6.5rem] @4xl:pb-5 @4xl:pl-[7rem] @4xl:pr-5"
        >
          <div className="min-w-0">
            {offer.priceInsightsVisible ? (
              <OfferPriceHistory
                offer={offer}
                currency={currency}
                size="compact"
                emptyState="monitoring_started"
              />
            ) : (
              <OfferRowMembersHistory />
            )}
          </div>
          <OfferRowPanelActions
            offer={offer}
            adminAction={adminAction}
            contextReleaseId={contextReleaseId}
          />
        </div>
      )}
    </li>
  );
}
